import { useTheme } from '@mui/material/styles'
import { useId } from 'react'

type SparklineProps = {
  points: number[]
  color?: string
  height?: number
}

/**
 * Tiny inline area sparkline. Lightweight SVG (no chart lib) since it's a
 * single decorative trend line inside a KPI card.
 */
export default function Sparkline({ points, color, height = 24 }: SparklineProps) {
  const theme = useTheme()
  const stroke = color ?? theme.affexy.colors.coral
  const gradientId = useId()

  if (points.length < 2) return null

  const w = 80
  const h = height
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const xs = points.map((_, i) => (i * w) / (points.length - 1))
  const ys = points.map((p) => h - ((p - min) / range) * (h - 4) - 2)
  const line = points.map((_, i) => `${i ? 'L' : 'M'}${xs[i].toFixed(1)} ${ys[i].toFixed(1)}`).join(' ')
  const area = `${line} L${xs[xs.length - 1].toFixed(1)} ${h} L${xs[0].toFixed(1)} ${h} Z`

  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ flex: 1, minWidth: 0 }}
      aria-hidden
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={stroke} stopOpacity="0.3" />
          <stop offset="1" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={line} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
